import React, { useState, useEffect, useRef } from 'react';
import httpHelper from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { Method_TYPE } from 'utils/http-helper';
import DataTable from 'react-data-table-component';
import ReportHeader from './ReportHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { convertToIST } from 'utils/formtaDate';
import {
  useFilteredSortedData,
  ReportTableControls,
  ExportButtons,
} from './reportTableUtils';

const SEARCHABLE_FIELDS = ['district', 'state', 'registered'];

export default function DistrictReg() {
  const [districtRegDetails, setDistrictRegDetails] = useState([]);
  const printRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');
  const [stateCountTNN, setStateCountTNN] = useState(0);
  const [stateCountTNS, setStateCountTNS] = useState(0);
  const [stateCount, setStateCount] = useState(0);

  useEffect(() => {
    getDistrictRegDetails();
  }, []);

  const getDistrictRegDetails = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_DISTRICT_REGISTATION, Method_TYPE.GET);
      const resultSet = data.map((tableData) => ({
        district: tableData?.district,
        state: tableData?.state,
        registered: tableData?.regCount,
      }));
      setDistrictRegDetails(resultSet);
      if (resultSet.length > 0) {
        const totalRegisteredTNN = resultSet.filter(x => x.state === 'TNN').reduce((acc, curr) => acc + curr.registered, 0);
        const totalRegisteredTNS = resultSet.filter(x => x.state === 'TNS').reduce((acc, curr) => acc + curr.registered, 0);
        setStateCountTNN(totalRegisteredTNN);
        setStateCountTNS(totalRegisteredTNS);
        setStateCount(totalRegisteredTNN + totalRegisteredTNS);
      }
    } catch (err) {
      console.error('Error fetching district registration details:', err);
    }
  };

  const filteredData = useFilteredSortedData(districtRegDetails, {
    searchTerm,
    searchableFields: SEARCHABLE_FIELDS,
    sortBy: null,
  });

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'));
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`;
    }
    return '';
  };

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    { name: 'District', selector: row => row.district, sortable: true, cell: row => <div data-title="District">{row.district}</div> },
    { name: 'State', selector: row => row.state, sortable: true, cell: row => <div data-title="State">{row.state}</div> },
    { name: 'Registered', selector: row => row.registered, sortable: true, cell: row => <div data-title="Registered">{row.registered}</div> },
  ];

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write('<html><head><title>District-wise Registration</title>');
    printWindow.document.write('<style>table { width: 100%; border-collapse: collapse; } td, th { border: 1px solid black; padding: 8px; }</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - District-wise Registration</h2><p><strong>${getBatchInfo()}</strong></p>`);
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text('District-wise Registration', 14, 10);
    doc.text(`${getBatchInfo()}`, 14, 18);
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'District', 'State', 'Registered']],
      body: filteredData.map((item, index) => [index + 1, item.district, item.state, item.registered]),
    });
    doc.save('district-wise-registration.pdf');
  };

  const handleDownloadXLS = () => {
    const exportData = filteredData.map((item, index) => ({
      'Sl.No': index + 1,
      District: item.district,
      State: item.state,
      Registered: item.registered,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'District Reg');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(file, 'district-wise-registration.xlsx');
  };

  return (
    <>
      <p className="mb-2 font-semibold">Total Districts: {districtRegDetails.length}</p>
      <p className="mb-2 font-semibold">Total Sevadals Registered: {stateCount}</p>
      <p className="mb-2 font-semibold"> [Tamilnadu North:{stateCountTNN}] --- [Tamilnadu South:{stateCountTNS}]</p>

      <ReportTableControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search district, state, registered count..."
        totalCount={districtRegDetails.length}
        filteredCount={filteredData.length}
        actions={
          <ExportButtons
            show={districtRegDetails.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
      />

      <ReportHeader header='District wise Registration' />
      <DataTable
        columns={columns}
        data={filteredData}
        defaultSortFieldId={2}
        striped
        highlightOnHover
        fixedHeader
        fixedHeaderScrollHeight='500px'
        className='reports-table'
        noDataComponent={
          <div className="py-10 text-sm text-slate-500">
            {searchTerm.trim() ? 'No rows match your search' : 'No district registration data available'}
          </div>
        }
      />

      <div ref={printRef} className="hidden">
        <table>
          <thead>
            <tr>
              <th>Sl.No</th>
              <th>District</th>
              <th>State</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.district}</td>
                <td>{item.state}</td>
                <td>{item.registered}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
